import { supabase } from "@/integrations/supabase/client";

const API_URL = import.meta.env.VITE_API_URL as string | undefined;

if (!API_URL) {
  throw new Error("VITE_API_URL is not set — frontend cannot reach the backend");
}

const BASE = API_URL.replace(/\/$/, "");

export interface ApiErrorBody {
  error?: string;
  code?: string;
  message?: string;
  details?: unknown;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;

  constructor(status: number, body: ApiErrorBody | string | null) {
    const parsed: ApiErrorBody =
      typeof body === "string" || body == null
        ? { message: typeof body === "string" ? body : undefined }
        : body;
    super(parsed.message ?? parsed.error ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = parsed.code ?? parsed.error;
    this.details = parsed.details;
  }
}

async function getAccessToken(): Promise<string> {
  const first = await supabase.auth.getSession();
  const token = first.data.session?.access_token;
  if (token) return token;

  const refreshed = await supabase.auth.refreshSession();
  const refreshedToken = refreshed.data.session?.access_token;
  if (!refreshedToken) {
    throw new ApiError(401, { code: "no_session", message: "Not authenticated" });
  }
  return refreshedToken;
}

interface RequestOptions {
  method: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  signal?: AbortSignal;
  /** When true, do not attach an Authorization header (used pre-login). */
  anonymous?: boolean;
}

async function request<T>(path: string, opts: RequestOptions): Promise<T> {
  const headers: Record<string, string> = {};
  if (!opts.anonymous) {
    const token = await getAccessToken();
    headers.Authorization = `Bearer ${token}`;
  }
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const url = path.startsWith("http") ? path : `${BASE}${path.startsWith("/") ? "" : "/"}${path}`;

  const response = await fetch(url, {
    method: opts.method,
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const parsed = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, parsed ?? text);
  }

  return parsed as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: "GET", signal });
}

export function apiPost<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: "POST", body, signal });
}

export function apiPatch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: "PATCH", body, signal });
}

export function apiPut<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: "PUT", body, signal });
}

export function apiDelete<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: "DELETE", signal });
}

// Multipart variant. Used by /api/attachments POST (file upload). The browser
// sets the multipart boundary in Content-Type automatically, so we must NOT
// set Content-Type manually.
export async function apiPostMultipart<T>(
  path: string,
  formData: FormData,
  signal?: AbortSignal,
): Promise<T> {
  const token = await getAccessToken();
  const url = path.startsWith("http")
    ? path
    : `${BASE}${path.startsWith("/") ? "" : "/"}${path}`;

  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
    signal,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, parsed ?? text);
  }

  return parsed as T;
}

// Public/anonymous variants — no Authorization header. Used pre-login (invites,
// log-login on failed attempts).
export function apiGetPublic<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: "GET", signal, anonymous: true });
}

export function apiPostPublic<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: "POST", body, signal, anonymous: true });
}
